import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, of } from 'rxjs';
import { translate } from '@jsverse/transloco';
import { switchMap, tap, map, catchError, finalize } from 'rxjs/operators';
import { CabinetService } from 'src/app/cabinet/services/cabinet.service';
import { ICreateCabinetRequest, IGetCabinetResponse, IUpdateCabinetRequest } from 'src/app/cabinet/models/cabinet';
import { IBaseGetByComanyRequest, IBaseSearchRequest, IPagedResults, PageSize } from 'src/app/core/models/base';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { Router } from '@angular/router';
import { downloadBlob } from 'src/app/shared/helpers/fileHelper';
import { ICreateSlotRequest, IGetSlotResponse, IUpdateSlotRequest } from 'src/app/cabinet/models/slot';
import { SlotService } from 'src/app/cabinet/services/slot.service.';

export interface SlotState {
  slots: IGetSlotResponse[];
  slotCount: number;
  loading: boolean;
}

@Injectable()
export class SlotStore extends ComponentStore<SlotState> {

  // Initial state
  constructor(
    private cabinetService: CabinetService,
    private slotService: SlotService,
    private appnotificationService: AppNotificationService,
    private router: Router
  ) {
    super({ slots: null, slotCount: null, loading: false });
  }

  // Selectors
  readonly loading$ = this.select((state) => state.loading);
  readonly slots$: Observable<IGetSlotResponse[]> = this.select(state => state.slots);
  readonly slotCount$: Observable<number> = this.select(state => state.slots?.length);

  readonly getSlotsByCabinetId = this.effect<string>(cabinetId$ =>
    cabinetId$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(cabinetId =>
        this.cabinetService.getCabinetById(cabinetId).pipe(
          map(cabinetResponse => {
            // Ensure slots are transformed into an array (even if a single slot is returned)
            const slots = cabinetResponse.slots ? [cabinetResponse.slots] : [];
            return slots;
          }),
          tap(slots => {
            const slotsArray = Array.isArray(slots[0]) ? slots[0] : [slots[0]]; // If it's not an array, wrap it in an array

            const selectedSlots = this.selectSlots(slotsArray, slot => true);

            // Update the state with selected slots and their count
            this.patchState({ slots: selectedSlots, loading: false, slotCount: selectedSlots.length });
          }),
          catchError(err => {
            console.error('Failed to load cabinet or slots:', err);
            return of([]); // Return an empty array on error
          })
        )
      )
    )
  );

  // Helper method to filter or select specific slots (no-op if no filtering is required)
  selectSlots(slots: IGetSlotResponse[], criteria: (slot: IGetSlotResponse) => boolean): IGetSlotResponse[] {
    return slots.filter(criteria);
  }


  // Effect to create a new cabinet
  readonly createSlot = this.effect<ICreateSlotRequest>(slot$ =>
    slot$.pipe(
      switchMap(slot =>
        this.slotService.createSlot(slot).pipe(
          tap({
            next: (createdSlot) => {
              if (createdSlot) {
                // Update the state with the newly created slot
                this.patchState(state => ({
                  slots: [...state.slots, createdSlot], // Append the new slot
                  slotCount: state.slots.length
                }));

                this.appnotificationService.notification$.next({
                  message: translate('slots.createSuccess'),
                  style: 'success'
                });
              }
            }
          })
        )
      )
    )
  );

  // Effect to delete a slot
  readonly deleteSlot = this.effect<string>(slotId$ =>
    slotId$.pipe(
      switchMap(slotId =>
        this.slotService.deleteSlot(slotId).pipe(
          tap({
            next: () => {
              // Remove the slot from the state
              this.patchState(state => ({
                slots: state.slots.filter(slot => slot.id !== slotId),
                slotCount: state.slots.length
              }));

              this.appnotificationService.notification$.next({
                message: translate('slots.deleteSuccess'),
                style: 'success'
              });
            },
            error: (err) => {
              this.appnotificationService.notification$.next({
                message: translate('slots.deleteFailed'),
                style: 'error'
              });
            }
          })
        )
      )
    )
  );

  // Effect to update a slot
  readonly updateSlot = this.effect<IUpdateSlotRequest>(slot$ =>
    slot$.pipe(
      switchMap(slot =>
        this.slotService.updateSlot(slot).pipe(
          tap({
            next: (updatedSlot) => {
              if (updatedSlot) {
                // Update the state with the updated slot
                this.patchState(state => ({
                  slots: state.slots.map(existingSlot =>
                    existingSlot.id === updatedSlot.id ? updatedSlot : existingSlot
                  ), // Replace the old slot with the updated one
                  slotCount: state.slots.length
                }));

                // Show success notification
                this.appnotificationService.notification$.next({
                  message: translate('slots.updateSuccess'),
                  style: 'success'
                });
              }
            },
            error: (err) => {
              this.appnotificationService.notification$.next({
                message: translate('slots.updateFailed'),
                style: 'error'
              });
            }
          })
        )
      )
    )
  );



  // Updater to set the loading state
  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
    error: undefined, // Clear error when starting a new process
  }));

  readonly logStateChanges = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => {
        this.state$.subscribe(state => console.log('ComponentStore State:', state));
      })
    )
  );

}
